import { Link } from 'react-router-dom';
import { AlertTriangle, ListChecks, Sparkles, CalendarClock } from 'lucide-react';
import { useMorningBriefing } from '@/hooks/useMorningBriefing';
import { useLanguage } from '@/contexts/LanguageContext';

const t = {
  en: {
    title: 'Morning Briefing',
    urgent: 'Urgent actions',
    today: 'To handle today',
    news: 'Last 24h',
    meetings: "Today's meetings",
    empty: 'Nothing here.',
    overdue: (h: number) => `${h}h overdue`,
    loading: 'Loading briefing…',
    newSubmission: 'New submission',
    ndaSigned: 'NDA signed',
    unreadMessage: 'Unread message',
  },
  fr: {
    title: 'Briefing du jour',
    urgent: 'Actions urgentes',
    today: 'À traiter aujourd’hui',
    news: 'Nouveautés 24h',
    meetings: 'Réunions du jour',
    empty: 'Rien à afficher.',
    overdue: (h: number) => `${h}h de retard`,
    loading: 'Chargement du briefing…',
    newSubmission: 'Nouvelle soumission',
    ndaSigned: 'NDA signé',
    unreadMessage: 'Message non lu',
  },
} as const;

function Card({ icon, title, accent, children }: {
  icon: React.ReactNode; title: string; accent: string; children: React.ReactNode;
}) {
  return (
    <div className="bg-gray-800/50 border border-gray-700/60 rounded-xl p-4 flex flex-col">
      <div className={`flex items-center gap-2 mb-3 ${accent}`}>
        {icon}<h3 className="font-semibold text-sm uppercase tracking-wide">{title}</h3>
      </div>
      <div className="flex-1 space-y-2">{children}</div>
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return <p className="text-xs text-gray-500 italic">{label}</p>;
}

function Row({ to, primary, secondary }: { to?: string; primary: string; secondary?: string }) {
  const content = (
    <div className="flex justify-between items-start gap-3 py-1.5 border-b border-gray-700/40 last:border-0">
      <span className="text-sm text-gray-200 truncate">{primary}</span>
      {secondary && <span className="text-xs text-gray-400 shrink-0">{secondary}</span>}
    </div>
  );
  return to ? <Link to={to} className="block hover:bg-gray-700/30 rounded px-1 -mx-1">{content}</Link> : content;
}

export function MorningBriefing() {
  const { language } = useLanguage();
  const L = t[language] || t.en;
  const { urgent, today, news, meetings, loading, error } = useMorningBriefing();

  if (loading) {
    return (
      <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4">
        <p className="text-gray-400 animate-pulse text-sm">{L.loading}</p>
      </div>
    );
  }

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-white">{L.title}</h2>
      {error && <p className="text-xs text-red-400">{error}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <Card icon={<AlertTriangle className="w-4 h-4" />} title={`🔴 ${L.urgent}`} accent="text-red-400">
          {urgent.length === 0 ? <Empty label={L.empty} /> : urgent.slice(0, 6).map((d) => (
            <Row key={d.id} to={`/staff/deals/${d.id}`} primary={d.title} secondary={L.overdue(d.hoursOverdue ?? 0)} />
          ))}
        </Card>

        <Card icon={<ListChecks className="w-4 h-4" />} title={`🟡 ${L.today}`} accent="text-amber-400">
          {today.length === 0 ? <Empty label={L.empty} /> : today.slice(0, 6).map((d) => (
            <Row key={d.id} to={`/staff/deals/${d.id}`} primary={d.title} secondary={d.status} />
          ))}
        </Card>

        <Card icon={<Sparkles className="w-4 h-4" />} title={`🆕 ${L.news}`} accent="text-sky-400">
          {news.length === 0 ? <Empty label={L.empty} /> : news.slice(0, 6).map((n) => {
            const label = n.type === 'new_submission' ? L.newSubmission
              : n.type === 'nda_signed' ? L.ndaSigned : L.unreadMessage;
            return (
              <Row
                key={n.id}
                to={n.dealId ? `/staff/deals/${n.dealId}` : undefined}
                primary={n.title}
                secondary={label}
              />
            );
          })}
        </Card>

        <Card icon={<CalendarClock className="w-4 h-4" />} title={`📅 ${L.meetings}`} accent="text-emerald-400">
          {meetings.length === 0 ? <Empty label={L.empty} /> : meetings.slice(0, 6).map((m) => (
            <Row
              key={m.id}
              to={m.dealId ? `/staff/deals/${m.dealId}` : undefined}
              primary={m.title}
              secondary={m.scheduledAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            />
          ))}
        </Card>
      </div>
    </section>
  );
}

export default MorningBriefing;
